import {
  queryGuests as publicQueryGuests,
  typedQueryGuests as publicTypedQueryGuests,
} from './events-guests-v1-guest-guests.public.js';
import { createRESTModule } from '@wix/sdk-runtime/rest-modules';
import { createEventModule } from '@wix/sdk-runtime/event-definition-modules';
import {
  BuildRESTFunction,
  MaybeContext,
  BuildEventDefinition,
} from '@wix/sdk-types';
import { HttpClient } from '@wix/sdk-types';
import { createQueryOverloadRouter } from '@wix/sdk-runtime/query-method-router';
import {
  EventGuestQuery,
  GuestsQueryBuilder,
  QueryEventGuestsOptions,
  typedQueryGuests as universalTypedQueryGuests,
} from './events-guests-v1-guest-guests.universal.js';
import { onGuestCreated as publicOnGuestCreated } from './events-guests-v1-guest-guests.public.js';
import { onGuestDeleted as publicOnGuestDeleted } from './events-guests-v1-guest-guests.public.js';
import { onGuestCheckedIn as publicOnGuestCheckedIn } from './events-guests-v1-guest-guests.public.js';
import { onGuestEventCanceled as publicOnGuestEventCanceled } from './events-guests-v1-guest-guests.public.js';
import { onGuestEventStarts as publicOnGuestEventStarts } from './events-guests-v1-guest-guests.public.js';
import { onGuestOrderCanceled as publicOnGuestOrderCanceled } from './events-guests-v1-guest-guests.public.js';
import { onGuestUpdated as publicOnGuestUpdated } from './events-guests-v1-guest-guests.public.js';

function customQueryGuests(httpClient: HttpClient) {
  const router = createQueryOverloadRouter({
    builderQueryFunction: (options?: QueryEventGuestsOptions) =>
      publicQueryGuests(httpClient)(options),
    typedQueryFunction: (
      query: EventGuestQuery,
      options?: QueryEventGuestsOptions
    ) => publicTypedQueryGuests(httpClient)(query, options),
    hasOptionsParameter: true,
  });

  function overloadedQuery(
    query: EventGuestQuery,
    options?: QueryEventGuestsOptions
  ): ReturnType<typeof universalTypedQueryGuests>;
  function overloadedQuery(
    options?: QueryEventGuestsOptions
  ): GuestsQueryBuilder;
  function overloadedQuery(
    queryOrOptions?: EventGuestQuery | QueryEventGuestsOptions,
    options?: QueryEventGuestsOptions
  ): any {
    return router(...arguments);
  }

  return overloadedQuery;
}

export const queryGuests: MaybeContext<
  BuildRESTFunction<typeof customQueryGuests> & typeof customQueryGuests
> = /*#__PURE__*/ createRESTModule(customQueryGuests);
/**
 * Triggered when a guest is created.
 */
export const onGuestCreated: BuildEventDefinition<typeof publicOnGuestCreated> &
  typeof publicOnGuestCreated = createEventModule(publicOnGuestCreated);
/**
 * Triggered when a guest is deleted.
 */
export const onGuestDeleted: BuildEventDefinition<typeof publicOnGuestDeleted> &
  typeof publicOnGuestDeleted = createEventModule(publicOnGuestDeleted);
/**
 * Triggered when a guest has checked in.
 */
export const onGuestCheckedIn: BuildEventDefinition<
  typeof publicOnGuestCheckedIn
> &
  typeof publicOnGuestCheckedIn = createEventModule(publicOnGuestCheckedIn);
/**
 * Triggered when an event is canceled.
 */
export const onGuestEventCanceled: BuildEventDefinition<
  typeof publicOnGuestEventCanceled
> &
  typeof publicOnGuestEventCanceled = createEventModule(
  publicOnGuestEventCanceled
);
/**
 * Triggered when an event has started.
 */
export const onGuestEventStarts: BuildEventDefinition<
  typeof publicOnGuestEventStarts
> &
  typeof publicOnGuestEventStarts = createEventModule(publicOnGuestEventStarts);
/**
 * Triggered when a ticket order is canceled.
 */
export const onGuestOrderCanceled: BuildEventDefinition<
  typeof publicOnGuestOrderCanceled
> &
  typeof publicOnGuestOrderCanceled = createEventModule(
  publicOnGuestOrderCanceled
);
/**
 * Triggered when a guest is updated.
 *
 * This event always triggers together with the Guest Created and the Guest Deleted events.
 * > **Note:** Since this event may trigger twice, you should explicitly add code to ensure that the parts of your code that depend on this event run only once.
 */
export const onGuestUpdated: BuildEventDefinition<typeof publicOnGuestUpdated> &
  typeof publicOnGuestUpdated = createEventModule(publicOnGuestUpdated);

export {
  SubdivisionType,
  AttendanceStatus,
  GuestType,
  RsvpStatus,
  OrderStatus,
  MemberEventStatusUpdatedEventType,
  EventType,
  LocationType,
  SubdivisionSubdivisionType,
  RecurrenceStatusStatus,
  Status,
  NotifyActionType,
  Timing,
  TicketingOrderStatus,
  TaxType,
  FeeName,
  FeeType,
  WebhookIdentityType,
  SortOrder,
  RequestedFieldsEnumRequestedFields,
  OrderType,
  RequestedFields,
} from './events-guests-v1-guest-guests.universal.js';
export {
  EventGuest,
  TicketDetails,
  TicketGuestDetails,
  OnlineConferencingLogin,
  GuestDetails,
  FormResponse,
  InputValue,
  FormattedAddress,
  Address,
  AddressStreetOneOf,
  StreetAddress,
  AddressLocation,
  Subdivision,
  StandardDetails,
  FormSubmission,
  AdditionalDetails,
  GuestCountUpdated,
  GuestCountUpdate,
  MemberEventStatusUpdated,
  ContactEventStatusUpdated,
  GuestCheckedIn,
  EventDetails,
  Location,
  CommonAddress,
  CommonAddressStreetOneOf,
  CommonStreetAddress,
  CommonAddressLocation,
  CommonSubdivision,
  DateAndTimeSettings,
  Recurrences,
  Occurrence,
  Formatted,
  NotifyGuestAction,
  TaskContext,
  OrderDetails,
  Invoice,
  Item,
  Money,
  Discount,
  DiscountItem,
  DiscountItemDiscountOneOf,
  CouponDiscount,
  PaidPlanDiscount,
  PaidPlanDiscountDiscountOneOf,
  PercentDiscount,
  Tax,
  Fee,
  GuestEventCanceled,
  GuestEventStarts,
  GuestOrderCanceled,
  GuestOrderConfirmed,
  DomainEvent,
  DomainEventBodyOneOf,
  EntityCreatedEvent,
  RestoreInfo,
  EntityUpdatedEvent,
  EntityDeletedEvent,
  ActionEvent,
  MessageEnvelope,
  IdentificationData,
  IdentificationDataIdOneOf,
  AccountInfo,
  QueryEventGuestsRequest,
  QueryV2,
  QueryV2PagingMethodOneOf,
  Sorting,
  Paging,
  CursorPaging,
  QueryEventGuestsResponse,
  PagingMetadataV2,
  Cursors,
  StreamEventGuestsRequest,
  Empty,
  SecondaryLanguagesRequest,
  SecondaryLanguagesResponse,
  Language,
  OrderConfirmed,
  Ticket,
  CheckIn,
  OrderUpdated,
  OrderDeleted,
  EventDeleted,
  Task,
  TaskKey,
  ListGuestListPreviewsRequest,
  ListGuestListPreviewsResponse,
  GuestListPreview,
  GuestCount,
  MemberGuests,
  MemberGuest,
  UpdateGuestRequest,
  UpdateGuestResponse,
  BaseEventMetadata,
  EventMetadata,
  AccountInfoMetadata,
  GuestCreatedEnvelope,
  GuestDeletedEnvelope,
  GuestCheckedInEnvelope,
  GuestEventCanceledEnvelope,
  GuestEventStartsEnvelope,
  GuestOrderCanceledEnvelope,
  GuestUpdatedEnvelope,
  QueryEventGuestsOptions,
  GuestsQueryResult,
  GuestsQueryBuilder,
  EventGuestQuerySpec,
} from './events-guests-v1-guest-guests.universal.js';
export { utils } from './events-guests-v1-guest-guests.universal.js';
export {
  SubdivisionTypeWithLiterals,
  AttendanceStatusWithLiterals,
  GuestTypeWithLiterals,
  RsvpStatusWithLiterals,
  OrderStatusWithLiterals,
  MemberEventStatusUpdatedEventTypeWithLiterals,
  EventTypeWithLiterals,
  LocationTypeWithLiterals,
  SubdivisionSubdivisionTypeWithLiterals,
  RecurrenceStatusStatusWithLiterals,
  StatusWithLiterals,
  NotifyActionTypeWithLiterals,
  TimingWithLiterals,
  TicketingOrderStatusWithLiterals,
  TaxTypeWithLiterals,
  FeeNameWithLiterals,
  FeeTypeWithLiterals,
  WebhookIdentityTypeWithLiterals,
  SortOrderWithLiterals,
  RequestedFieldsEnumRequestedFieldsWithLiterals,
  OrderTypeWithLiterals,
  RequestedFieldsWithLiterals,
  CommonQueryWithEntityContext,
  EventGuestQuery,
} from './events-guests-v1-guest-guests.universal.js';
